import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { notificationService } from "../services/notifications/notificationService";
import { supportService } from "../services/supportService";
import { supabase } from "../supabaseClient";
import { useJWTAuth } from "./JWTAuthContext";
import logger from "../utils/logger";

const NotificationsContext = createContext();
const INITIAL_NOTIFICATIONS_BOOTSTRAP_DELAY_MS = 1200;

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationsProvider",
    );
  }
  return context;
};

export const NotificationsProvider = ({ children }) => {
  const { user, getUserRole } = useJWTAuth();
  const userRole = getUserRole();
  const isSupportAdmin = userRole === "megaadmin";

  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadSupportCount, setUnreadSupportCount] = useState(0);
  const [paymentRequestCount, setPaymentRequestCount] = useState(0);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);

  // Загрузка количества непрочитанных уведомлений
  const loadUnreadCount = useCallback(async () => {
    if (!user?.id) return;

    try {
      const [count, supportCount, paymentReqCount] = await Promise.all([
        notificationService.getUnreadCount(),
        isSupportAdmin
          ? supportService.getAdminUnreadCount() // Admin: непрочитанные от пользователей
          : supportService.getUserUnreadCount(), // User: непрочитанные от админа
        isSupportAdmin
          ? supabase
              .rpc("count_pending_payment_requests")
              .then(({ data, error }) => (error ? 0 : data || 0))
              .catch(() => 0)
          : Promise.resolve(0),
      ]);

      setUnreadCount(count);
      setUnreadSupportCount(supportCount);
      setPaymentRequestCount(paymentReqCount || 0);
    } catch (error) {
      logger.error("Error loading unread count", error);
      setUnreadCount(0);
      setUnreadSupportCount(0);
      setPaymentRequestCount(0);
    }
  }, [user?.id, isSupportAdmin]);

  const loadRecentNotifications = useCallback(async () => {
    if (!user?.id) return;

    try {
      const notifications = await notificationService.getRecentNotifications(5);
      setRecentNotifications(
        notifications.map((n) => notificationService.formatNotification(n)),
      );
    } catch (error) {
      logger.error("Error loading recent notifications", error);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    if (recentNotifications.length === 0) {
      const cached = notificationService._getFromCache();
      if (cached) {
        setRecentNotifications(
          cached
            .slice(0, 5)
            .map((notification) =>
              notificationService.formatNotification(notification),
            ),
        );
      }
    }

    const timerId = window.setTimeout(() => {
      loadUnreadCount();
    }, INITIAL_NOTIFICATIONS_BOOTSTRAP_DELAY_MS);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [user?.id, loadUnreadCount, recentNotifications.length]);

  useEffect(() => {
    if (!user?.id || !isOpen) return;

    loadRecentNotifications();
  }, [user?.id, isOpen, loadRecentNotifications]);

  // Listen for chat view updates from SupportChatPage
  useEffect(() => {
    if (!user?.id) return;

    const handleChatViewed = (event) => {
      // Перезагружаем счетчик непрочитанных при просмотре чата
      // С небольшой задержкой, чтобы БД успела обновиться
      setTimeout(() => {
        loadUnreadCount();
      }, 300);
    };

    window.addEventListener("support-chat-viewed", handleChatViewed);
    return () =>
      window.removeEventListener("support-chat-viewed", handleChatViewed);
  }, [user?.id, loadUnreadCount]);

  useEffect(() => {
    if (!user?.id) return;

    const interval = setInterval(() => {
      loadUnreadCount();
      if (isOpen) {
        loadRecentNotifications();
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [user?.id, isOpen, loadUnreadCount, loadRecentNotifications]);

  // Вычисление позиции dropdown при открытии
  const calculateDropdownPosition = useCallback(() => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const sidebar = document.querySelector(".sidebar");
    const sidebarRect = sidebar ? sidebar.getBoundingClientRect() : null;

    // Определяем, свернут ли сайдбар
    const isSidebarCollapsed =
      sidebar && sidebar.classList.contains("sidebar-collapsed");

    // Базовые координаты - dropdown должен появляться СПРАВА ОТ SIDEBAR
    let left, top;

    if (isSidebarCollapsed && sidebarRect) {
      // В свернутом сайдбаре - появляемся справа от края сайдбара
      left = sidebarRect.right + 8; // 8px отступ от правого края сайдбара
      top = buttonRect.top;
    } else if (sidebarRect) {
      // В развернутом сайдбаре - появляемся справа от края сайдбара
      left = sidebarRect.right + 8; // 8px отступ от правого края сайдбара
      top = buttonRect.top;
    } else {
      // Fallback - если сайдбар не найден
      left = buttonRect.right + 8;
      top = buttonRect.top;
    }

    // Проверяем, не выходит ли dropdown за пределы экрана справа
    const dropdownWidth = 300;
    const viewportWidth = window.innerWidth;
    if (left + dropdownWidth > viewportWidth) {
      left = viewportWidth - dropdownWidth - 16; // 16px отступ от края экрана
    }

    // Проверяем, не выходит ли dropdown за пределы экрана снизу
    const dropdownHeight = 400;
    const viewportHeight = window.innerHeight;
    if (top + dropdownHeight > viewportHeight) {
      top = buttonRect.top - dropdownHeight - 8; // Показываем выше кнопки
    }

    setDropdownPosition({ left, top });
    logger.debug("Dropdown position calculated", {
      left,
      top,
      isSidebarCollapsed,
      sidebarRight: sidebarRect?.right,
      buttonRight: buttonRect.right,
      positioning: "RIGHT_OF_SIDEBAR_GLOBAL",
    });
  }, []);

  // Закрытие dropdown при клике вне его
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target)) {
        // Проверяем, что клик не по dropdown (он будет на уровне Layout)
        const dropdown = document.getElementById(
          "global-notifications-dropdown",
        );
        if (dropdown && !dropdown.contains(event.target)) {
          setIsOpen(false);
        }
      }
    };

    const handleResize = () => {
      if (isOpen) {
        calculateDropdownPosition();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("resize", handleResize);
      calculateDropdownPosition();
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen, calculateDropdownPosition]);

  // Переключение dropdown
  const toggleNotifications = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      setIsOpen((prev) => !prev);
    },
    [],
  );

  // Отметить уведомление как прочитанное
  const handleMarkAsRead = useCallback(
    async (notificationId) => {
      try {
        const success = await notificationService.markAsRead(notificationId);
        if (success) {
          setRecentNotifications((prev) =>
            prev.map((n) =>
              n.id === notificationId ? { ...n, is_read: true } : n,
            ),
          );
          loadUnreadCount();
        }
      } catch (error) {
        logger.error("Error marking notification as read", error);
      }
    },
    [loadUnreadCount],
  );

  // Отметить все как прочитанные
  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();
      setRecentNotifications((prev) =>
        prev.map((n) => ({ ...n, is_read: true })),
      );
      setUnreadCount(0);

      setTimeout(() => {
        loadUnreadCount();
      }, 1000);
    } catch (error) {
      logger.error("Error marking all notifications as read", error);
      loadUnreadCount();
    }
  }, [loadUnreadCount]);

  // Удаление одного уведомления
  const handleDeleteNotification = useCallback(
    async (notificationId) => {
      try {
        const success =
          await notificationService.deleteNotification(notificationId);
        if (success) {
          setRecentNotifications((prev) =>
            prev.filter((n) => n.id !== notificationId),
          );
          loadUnreadCount();
          return true;
        }
        return false;
      } catch (error) {
        logger.error("Error deleting notification", error);
        return false;
      }
    },
    [loadUnreadCount],
  );

  // Массовое удаление уведомлений
  const handleBulkDeleteNotifications = useCallback(
    async (notificationIds) => {
      try {
        const success = await notificationService.bulkDelete(notificationIds);
        if (success) {
          setRecentNotifications((prev) =>
            prev.filter((n) => !notificationIds.includes(n.id)),
          );
          loadUnreadCount();
          return true;
        }
        return false;
      } catch (error) {
        logger.error("Error bulk deleting notifications", error);
        return false;
      }
    },
    [loadUnreadCount],
  );

  const value = useMemo(
    () => ({
      unreadCount,
      unreadSupportCount,
      paymentRequestCount,
      recentNotifications,
      isOpen,
      dropdownPosition,
      buttonRef,
      toggleNotifications,
      calculateDropdownPosition,
      handleMarkAsRead,
      handleMarkAllAsRead,
      handleDeleteNotification,
      handleBulkDeleteNotifications,
      loadUnreadCount,
      loadRecentNotifications,
    }),
    [
      unreadCount,
      unreadSupportCount,
      paymentRequestCount,
      recentNotifications,
      isOpen,
      dropdownPosition,
      toggleNotifications,
      calculateDropdownPosition,
      handleMarkAsRead,
      handleMarkAllAsRead,
      handleDeleteNotification,
      handleBulkDeleteNotifications,
      loadUnreadCount,
      loadRecentNotifications,
    ],
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};
